import { JSX } from "react";
import { Page, View, Document, Image } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { FontSizeProvider } from "components/Resume/ResumePDF/FontSizeContext";
import { ResumePDFProfile } from "components/Resume/ResumePDF/ResumePDFProfile";
import { ResumePDFWorkExperience } from "components/Resume/ResumePDF/ResumePDFWorkExperience";
import { ResumePDFEducation } from "components/Resume/ResumePDF/ResumePDFEducation";
import { ResumePDFProject } from "components/Resume/ResumePDF/ResumePDFProject";
import { ResumePDFSkills } from "components/Resume/ResumePDF/ResumePDFSkills";
import { ResumePDFCustom } from "components/Resume/ResumePDF/ResumePDFCustom";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import type { Settings, ShowForm } from "lib/redux/settingsSlice";
import type { Resume } from "lib/redux/types";
import { SuppressResumePDFErrorMessage } from "components/Resume/ResumePDF/common/SuppressResumePDFErrorMessage";
import { ResumePDFCertificate } from "../ResumePDF/ResumePDFCertificate";
import { ResumePDFLanguage } from "../ResumePDF/ResumePDFLanguage";
import { ResumePDFAward } from "../ResumePDF/ResumePDFAward";
import { Profile } from "./Profile";
import { ProfileImage } from "./common";
import { Summary } from "./Summary";
import { useEffect, useState } from "react";

export const TemplateTwenty = ({
  resume,
  settings,
  isPDF = false,
  userProfilePhoto,
}: {
  resume: Resume;
  settings: Settings;
  isPDF?: boolean;
  userProfilePhoto?: string | null;
}) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    skills,
    certifications,
    languages,
    awards,
    custom,
  } = resume;
  const { name } = profile;
  const {
    fontFamily,
    fontSize,
    documentSize,
    formToHeading,
    formToShow,
    formsOrder,
    showBulletPoints,
  } = settings;
  const themeColor = settings.themeColor || DEFAULT_FONT_COLOR;
  const profileImageURL = resume.profileImageUrl;
  const [profilePhoto, setProfilePhoto] = useState(
    localStorage.getItem("profile-photo")
  );

  useEffect(() => {
    setProfilePhoto(localStorage.getItem("profile-photo"));
  }, [resume.profileStatus, userProfilePhoto]);

  const showFormsOrder = formsOrder.filter((form) => formToShow[form]);

  const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
    workExperiences: () => (
      <ResumePDFWorkExperience
        heading={formToHeading["workExperiences"]}
        workExperiences={workExperiences}
        themeColor={themeColor}
      />
    ),
    educations: () => (
      <ResumePDFEducation
        heading={formToHeading["educations"]}
        educations={educations}
        themeColor={themeColor}
        showBulletPoints={showBulletPoints["educations"]}
      />
    ),
    projects: () => (
      <ResumePDFProject
        heading={formToHeading["projects"]}
        projects={projects}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    skills: () => (
      <ResumePDFSkills
        heading={formToHeading["skills"]}
        skills={skills}
        themeColor={themeColor}
        showBulletPoints={showBulletPoints["skills"]}
      />
    ),
    certifications: () => (
      <ResumePDFCertificate
        heading={formToHeading["certifications"]}
        certifications={certifications}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    languages: () => (
      <ResumePDFLanguage
        heading={formToHeading["languages"]}
        languages={languages}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    awards: () => (
      <ResumePDFAward
        heading={formToHeading["awards"]}
        awards={awards}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    custom: () => (
      <ResumePDFCustom
        heading={formToHeading["custom"]}
        custom={custom}
        themeColor={themeColor}
        showBulletPoints={showBulletPoints["custom"]}
      />
    ),
  };

  return (
    <>
      <Document title={`${name} Resume`} author={name} producer={"OpenResume"}>
        <Page
          size={documentSize === "A4" ? "A4" : "LETTER"}
          style={{
            ...styles.flexCol,
            color: DEFAULT_FONT_COLOR,
            fontFamily,
            fontSize: fontSize + "pt",
          }}
        >
          <FontSizeProvider fontSize={fontSize}>
            <View style={{ paddingTop: `${spacing[5]}` }} fixed></View>
            <View
              style={{
                ...styles.flexCol,
                padding: `${spacing[0]} ${spacing[20]}`,
                paddingTop: `${spacing[5]}`,
              }}
            >
              <View style={{ ...styles.flexRow }}>
                <Profile
                  profile={profile}
                  themeColor={themeColor}
                  isPDF={isPDF}
                />
                {/* {profilePhoto && (
                  <ProfileImage
                    // src="/assets/samplePP.jpeg"
                    // src={profileImageURL!}
                    src={profilePhoto}
                    alt={profile.name}
                    style={{ width: "50%" }}
                  />
                )} */}
                {userProfilePhoto && (
                  <ProfileImage
                    src={userProfilePhoto}
                    // src={profilePhoto!}
                    alt={profile.name}
                    style={{ width: "50%" }}
                  />
                )}
              </View>
              <Summary
                heading="Profile"
                summary={profile.summary}
                themeColor={themeColor}
              />
              {showFormsOrder.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
            <View style={{ paddingBottom: `${spacing[5]}` }} fixed></View>
          </FontSizeProvider>
        </Page>
      </Document>
      <SuppressResumePDFErrorMessage />
    </>
  );
};
